import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { BtnPrimary, BtnSecondary } from "@/pages/Senders";
import { ArrowLeft, DownloadSimple, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * Inbox Placement — detail page. Auto-refreshes every 5s until the test
 * reaches "complete". Lets you force an extra poll cycle, and export a
 * CSV of every seed_email × folder result.
 */
export default function InboxPlacementDetail() {
  const { id } = useParams();
  const [test, setTest] = useState(null);
  const [results, setResults] = useState([]);
  const [polling, setPolling] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get(`/inbox-placement/${id}`);
      setTest(r.data.test);
      setResults(r.data.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const iv = setInterval(() => {
      // stop polling frontend once the test is complete
      if (test?.status === "complete" || test?.status === "failed") return;
      load();
    }, 5000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [id, test?.status]);

  const doPoll = async () => {
    setPolling(true);
    try {
      const { data } = await api.post(`/inbox-placement/${id}/poll`);
      toast.success(`Polled — ${data.polled} still awaiting placement`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Poll failed"); }
    finally { setPolling(false); }
  };

  const download = async () => {
    const token = localStorage.getItem("ce_token");
    const res = await fetch(`${API}/inbox-placement/${id}/export.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return toast.error(`Download failed: HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inbox_placement_${test?.name || id}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="p-8 font-mono text-[#71717A]">[ loading... ]</div>;
  if (!test) return <div className="p-8 font-mono text-[#FF3B30]">Test not found</div>;

  const counts = test.counts || {};
  const totalTargets = (test.sender_ids || []).length;
  const placed = (counts.inbox || 0) + (counts.spam || 0) + (counts.promotions || 0) +
    (counts.updates || 0) + (counts.social || 0) + (counts.forums || 0) + (counts.other || 0) + (counts.trash || 0);
  const inboxRate = counts.sent ? Math.round((counts.inbox || 0) / counts.sent * 100) : 0;
  const rotDone = test.rotations_completed || 0;
  const rotTotal = test.rotations_total || 0;

  return (
    <div className="p-8 max-w-6xl" data-testid="ip-detail-page">
      <div className="mb-4">
        <Link to="/inbox-placement" className="text-xs text-[#71717A] hover:text-white font-mono inline-flex items-center gap-1">
          <ArrowLeft size={11} weight="bold" /> back to placement tests
        </Link>
      </div>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-mono uppercase tracking-wider">{test.name}</h1>
          <div className="text-[11px] text-[#71717A] font-mono mt-1">
            [IPT-{test.tag_token}] · {test.subject}
          </div>
          <div className="text-[10px] text-[#71717A] font-mono mt-1">
            status: <span className="text-white">{test.status}</span> ·
            {test.started_at && ` started ${test.started_at.slice(0, 19)} · `}
            {test.completed_at && ` completed ${test.completed_at.slice(0, 19)}`}
          </div>
          {rotTotal > 0 && (
            <div className="text-[10px] text-[#71717A] font-mono mt-1" data-testid="ip-detail-rotation-progress">
              rotation <span className="text-[#E1FF00]">{rotDone}</span> of <span className="text-white">{rotTotal}</span>
              {test.rotation_size ? <> · size <span className="text-white">{test.rotation_size}</span></> : null}
              {test.rotation_delay_seconds ? <> · delay <span className="text-white">{test.rotation_delay_seconds}s</span></> : null}
              {test.poll_concurrency ? <> · poll×<span className="text-white">{test.poll_concurrency}</span></> : null}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <BtnSecondary onClick={doPoll} disabled={polling} testid="ip-detail-poll">
            <ArrowsClockwise size={13} weight="bold" /> {polling ? "Polling..." : "Poll now"}
          </BtnSecondary>
          <BtnPrimary onClick={download} testid="ip-detail-download">
            <DownloadSimple size={13} weight="bold" /> Download CSV
          </BtnPrimary>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 mb-6">
        <Tile label="Targets" value={totalTargets} accent="#71717A" />
        <Tile label="Sent" value={counts.sent || 0} accent="#00E5FF" />
        <Tile label="Placed" value={placed} accent="#00E5FF" />
        <Tile label="Inbox rate" value={`${inboxRate}%`} accent={inboxRate >= 70 ? "#00E5FF" : "#E1FF00"} />
      </div>

      <div className="grid grid-cols-4 gap-2 mb-8">
        <Tile label="Inbox" value={counts.inbox || 0} accent="#00E5FF" small />
        <Tile label="Spam" value={counts.spam || 0} accent="#FF3B30" small />
        <Tile label="Promotions" value={counts.promotions || 0} accent="#E1FF00" small />
        <Tile label="Updates" value={counts.updates || 0} accent="#71717A" small />
        <Tile label="Social" value={counts.social || 0} accent="#71717A" small />
        <Tile label="Forums" value={counts.forums || 0} accent="#71717A" small />
        <Tile label="Other" value={counts.other || 0} accent="#71717A" small />
        <Tile label="Not found" value={counts.not_found || 0} accent="#71717A" small />
      </div>

      <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
        <table className="dtable w-full" data-testid="ip-detail-results">
          <thead>
            <tr>
              <th>Seed email</th><th>Sender</th><th>Send status</th><th>Folder</th>
              <th>Delivered at</th><th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {results.length === 0 && (
              <tr><td colSpan={6} className="text-center text-[#71717A] py-8 font-mono">[ awaiting results... ]</td></tr>
            )}
            {results.map((r) => (
              <tr key={r.id}>
                <td className="font-mono text-xs">{r.seed_email}</td>
                <td className="font-mono text-[11px] text-[#71717A]">{r.sender_email}</td>
                <td className="font-mono text-xs">
                  <span style={{ color: r.send_status === "sent" ? "#00E5FF" : "#FF3B30" }}>{r.send_status || "pending"}</span>
                </td>
                <td className="font-mono text-xs"><FolderChip folder={r.folder} /></td>
                <td className="font-mono text-[10px] text-[#71717A]">{(r.delivered_at || "").slice(0, 19) || "—"}</td>
                <td className="font-mono text-[10px] text-[#71717A] max-w-md truncate" title={r.error || (r.labels || []).join(", ")}>
                  {r.error || (r.labels || []).join(", ") || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tile({ label, value, accent = "#00E5FF", small }) {
  return (
    <div className={`border border-[#2A2A2A] bg-[#0A0A0A] ${small ? "p-2" : "p-4"}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#71717A] font-mono">{label}</div>
      <div className={`font-mono ${small ? "text-lg" : "text-2xl"} mt-1`} style={{ color: accent }}>{value}</div>
    </div>
  );
}

function FolderChip({ folder }) {
  if (!folder) return <span className="text-[#71717A]">—</span>;
  const colors = {
    inbox: "#00E5FF", spam: "#FF3B30", promotions: "#E1FF00",
    updates: "#71717A", social: "#71717A", forums: "#71717A",
    trash: "#FF3B30", other: "#71717A", not_found: "#71717A",
  };
  const c = colors[folder] || "#71717A";
  return <span className="inline-block px-2 py-0.5 border text-[10px]" style={{ color: c, borderColor: `${c}55` }}>{folder}</span>;
}
